import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import { User } from '@/types';
import { Loader2, Plus, Search, WifiOff } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import AddUserDialog from './AddUserDialog';

const UserManagement = () => {
  const { users, isLoading, updateUserRole, getUsers, user: currentUser } = useAuth();
  const [showAddUser, setShowAddUser] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);

  // Monitor online status
  useEffect(() => {
    const handleOnline = async () => {
      setIsOffline(false);
      
      // Sync pending role changes
      try {
        setIsSyncing(true);
        toast({
          title: "Syncing Changes",
          description: "Synchronizing changes made while offline...",
        });
        
        await authService.syncPendingRoleChanges();
        
        toast({
          title: "Back Online",
          description: "Your connection has been restored and changes synchronized.",
        });
      } catch (error) {
        console.error('Error syncing changes:', error);
        toast({
          title: "Sync Error",
          description: "Could not synchronize all offline changes.",
          variant: "destructive"
        });
      } finally {
        setIsSyncing(false);
        // Refresh data when coming back online
        getUsers();
      }
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      toast({
        title: "Offline Mode",
        description: "You're now in offline mode. Changes will be stored locally and synced when you reconnect.",
        variant: "destructive"
      });
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [getUsers]);

  const handleRoleChange = async (userId: string, newRole: 'user' | 'admin') => {
    try {
      console.log(`Changing role for user ${userId} to ${newRole}...`);
      console.log(`Current user: ${currentUser?.id} (${currentUser?.email})`);
      console.log(`Users list contains ${users.length} users`);
      console.log(`Network status: ${navigator.onLine ? 'online' : 'offline'}`);
      
      // Check if user exists in the users list
      const targetUser = users.find(u => u.id === userId);
      if (!targetUser) {
        toast({
          title: "Error",
          description: `User with ID ${userId} not found`,
          variant: "destructive"
        });
        return;
      }
      
      console.log(`Target user: ${targetUser.id} (${targetUser.email}), current role: ${targetUser.role}`);
      
      // Check if current user is logged in
      if (!currentUser) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to change user roles",
          variant: "destructive"
        });
        return;
      }
      
      // Show different toast based on online status
      if (!navigator.onLine) {
        toast({
          title: "Offline Mode",
          description: "Role will be updated locally and synced when you're back online",
        });
      }
      
      await updateUserRole(userId, newRole);
      
      // Force a refresh of the users list
      setTimeout(() => {
        getUsers();
      }, 500);
      
      toast({
        title: "Success",
        description: navigator.onLine 
          ? `User role updated to ${newRole}` 
          : `User role updated to ${newRole} (offline mode)`,
      });
    } catch (error) {
      console.error("Role change error:", error);
      toast({
        title: "Error",
        description: error instanceof Error 
          ? `Failed to update user role: ${error.message}` 
          : "Failed to update user role: Unknown error",
        variant: "destructive"
      });
    }
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isOffline && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded shadow-md flex items-center space-x-2">
          <WifiOff className="h-5 w-5" />
          <span>
            <strong>Offline Mode:</strong> Changes will be saved locally and synchronized when you reconnect.
          </span>
        </div>
      )}
      
      {isSyncing && (
        <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 rounded shadow-md flex items-center space-x-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>
            <strong>Syncing:</strong> Synchronizing changes made while offline...
          </span>
        </div>
      )}
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>User Management</CardTitle>
            <CardDescription>Manage users and their permissions</CardDescription>
          </div>
          <Button onClick={() => setShowAddUser(true)} disabled={isOffline}>
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          {users.length === 0 ? (
            <div className="text-center p-8">
              <p className="text-muted-foreground">No users found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user, idx) => (
                  <TableRow key={`${user.id || user.email || idx}-${idx}`}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Select
                        value={user.role}
                        onValueChange={(value: 'user' | 'admin') => handleRoleChange(user.id, value)}
                        disabled={user.id === currentUser?.id}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {isOffline && user.role !== user._originalRole && (
                        <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                          Pending Sync
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <AddUserDialog open={showAddUser} onOpenChange={setShowAddUser} />
    </div>
  );
};

export default UserManagement;
